import { createReactBlockSpec, useEditorState } from "@blocknote/react"
import { useEffect, useId, useRef, useState } from "react"
import { Link } from "~/components/Link"
import { Button } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import { iframeBlockConfig, normalizeIframeUrl } from "~/lib/editor-content"

export const Iframe = createReactBlockSpec(iframeBlockConfig, {
	render: function IframeBlock({ block, editor }) {
		const editable = useEditorState({
			editor,
			selector: ({ editor }) => editor.isEditable
		})
		const [draft, setDraft] = useState(block.props.url)
		const [invalid, setInvalid] = useState(false)
		const errorId = useId()
		const embedButton = useRef<HTMLButtonElement>(null)
		const url = normalizeIframeUrl(block.props.url)

		useEffect(() => {
			setDraft(block.props.url)
			setInvalid(false)
		}, [block.props.url])

		return (
			<div
				contentEditable={false}
				className="flex w-full min-w-0 flex-col gap-2"
			>
				{editable && (
					<div className="flex flex-col gap-2">
						<div className="flex items-center gap-2">
							<Input
								type="url"
								aria-label="Iframe URL"
								placeholder="https://example.com/embed"
								value={draft}
								aria-invalid={invalid || undefined}
								aria-describedby={invalid ? errorId : undefined}
								onChange={(event) => {
									setDraft(event.target.value)
									setInvalid(false)
								}}
								onKeyDown={(event) => {
									event.stopPropagation()
									if (event.key === "Enter" && !event.nativeEvent.isComposing) {
										event.preventDefault()
										embedButton.current?.click()
									}
								}}
							/>
							<Button
								ref={embedButton}
								onClick={() => {
									if (!editor.isEditable) return
									const nextUrl = normalizeIframeUrl(draft)
									if (!nextUrl) {
										setInvalid(true)
										return
									}
									editor.updateBlock(block, { props: { url: nextUrl } })
									setDraft(nextUrl)
									setInvalid(false)
								}}
							>
								{url ? "Update" : "Embed"}
							</Button>
						</div>
						{invalid && (
							<p id={errorId} role="alert" className="text-sm text-destructive">
								Enter a valid HTTPS URL without credentials.
							</p>
						)}
					</div>
				)}
				{url ? (
					<>
						<iframe
							src={url}
							title={`Embedded content from ${new URL(url).hostname}`}
							className="aspect-video min-h-64 w-full rounded-md border border-stroke"
							loading="lazy"
							sandbox="allow-scripts allow-forms allow-popups"
							referrerPolicy="no-referrer"
							allowFullScreen
						/>
						<Link to={url} target="_blank" rel="noopener noreferrer">
							Open embed
						</Link>
					</>
				) : !editable ? (
					<p className="text-sm text-text-small">No valid iframe URL.</p>
				) : null}
			</div>
		)
	},
	// Portable snapshots use a link, not executable embedded HTML.
	toExternalHTML: ({ block }) => {
		const url = normalizeIframeUrl(block.props.url)
		return url ? <a href={url}>{url}</a> : <p />
	}
})
